"use client";

import { useEffect, useRef, useState } from "react";
import type { DotLottieWC } from "@lottiefiles/dotlottie-wc";

interface LottiePlayerProps {
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  className?: string;
  minDevicePixelRatio?: number;
  onReady?: () => void;
}

let dotLottiePromise: Promise<typeof import("@lottiefiles/dotlottie-wc")> | null = null;

function loadDotLottieModule() {
  if (typeof window === "undefined") return null;
  if (!dotLottiePromise) {
    dotLottiePromise = import("@lottiefiles/dotlottie-wc");
  }
  return dotLottiePromise;
}

// Start loading the Web Component bundle immediately on client evaluation
if (typeof window !== "undefined") {
  loadDotLottieModule();
}

export function LottiePlayer({
  src,
  autoplay = true,
  loop = true,
  className = "",
  minDevicePixelRatio,
  onReady,
}: LottiePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<DotLottieWC | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const modulePromise = loadDotLottieModule();
    if (!modulePromise) return;

    modulePromise.then((module) => {
      if (!container || playerRef.current) return;

      const player = new module.DotLottieWC();
      if (minDevicePixelRatio) {
        player.renderConfig = {
          autoResize: true,
          devicePixelRatio: Math.max(
            window.devicePixelRatio,
            minDevicePixelRatio,
          ),
          quality: 100,
        };
      }
      player.src = src;
      player.autoplay = autoplay;
      player.loop = loop;
      player.style.width = "100%";
      player.style.height = "100%";

      container.appendChild(player);
      playerRef.current = player;
      setIsReady(true);
      onReady?.();
    });

    return () => {
      if (playerRef.current && container) {
        container.removeChild(playerRef.current);
        playerRef.current = null;
      }
    };
  }, [src, autoplay, loop, minDevicePixelRatio, onReady]);

  return <div ref={containerRef} className={className} />;
}
